import type { AdminPedido, Config, Foto } from "@/state/types";
import { blobToDataUrl } from "@/state/api";
import {
  applyEdits,
  EditorOpsZod,
  loadImageToCanvas,
} from "@/editor/editorCore";
import { server } from "@/state/server";
import JSZip from "jszip";

export async function downloadZip(
  config: Config,
  pedido: AdminPedido,
  full: boolean,
  reportStatus: (s: string) => unknown
) {
  let done = 0;

  const statusPref = `Generando zip para pedido ${pedido.id}`;

  reportStatus(statusPref);
  const files = await mapLimit(pedido.fotos, 5, async (foto) => {
    const orig = await (
      await fetch(`${server}/admin/originales/${pedido.id}/${foto.i}`, {
        credentials: "include",
      })
    ).blob();
    const origB64 = await blobToDataUrl(orig);
    const res: { foto: Foto; orig: Blob; edited?: Blob } = { foto, orig };
    if (foto.edicion) {
      const ops = EditorOpsZod.parse(JSON.parse(foto.edicion));
      const canvas = document.createElement("canvas");
      const img = await loadImageToCanvas(origB64, canvas);
      applyEdits(canvas, img, ops);
      res.edited = await canvasToBlob(canvas);
    }
    done++;
    reportStatus(`${statusPref}: ${done}/${pedido.fotos.length} descargadas`);
    return res;
  });

  reportStatus(`${statusPref}: Generando zip`);

  const resumen =
    `Pedido ${pedido.codLocal} ${pedido.id}, de ${pedido.cliente.nombre} (${pedido.cliente.email})\n` +
    `Observaciones: ${pedido.obs}\n\n\n` +
    files
      .map(
        (f) =>
          `${f.foto.i}-${config.fotografos[f.foto.g]?.codigo}-${config.temporadas[f.foto.t].productos.find((p) => p.id === f.foto.producto)?.descripcion}-${f.foto.cantidad}.jpg\n`
      )
      .join("");

  const blob = await createZip([
    {
      name: "detalle.txt",
      content: new Blob([resumen], { type: "text/plain" }),
    },
    ...files.flatMap((f) => {
      const name = `${f.foto.i}-${config.fotografos[f.foto.g]?.codigo}`;
      return [
        {
          name: `${name}-${config.temporadas[f.foto.t].productos.find((p) => p.id === f.foto.producto)?.descripcion}-${f.foto.cantidad}.jpg`,
          content: f.edited ?? f.orig,
        },
        ...(f.foto.edicion
          ? [{ name: `${name}-original.jpg`, content: f.orig }]
          : []),
      ];
    }),
  ]);

  reportStatus("");

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${pedido.id} - ${pedido.cliente.nombre}${full ? "-todas" : ""}.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

async function mapLimit<T, R>(
  inputs: T[],
  limit: number,
  asyncMapper: (input: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  let i = 0;

  async function worker() {
    while (i < inputs.length) {
      const currentIndex = i++;
      results[currentIndex] = await asyncMapper(
        inputs[currentIndex],
        currentIndex
      );
    }
  }

  const workers = Array.from({ length: Math.min(limit, inputs.length) }, () =>
    worker()
  );

  await Promise.all(workers);
  return results;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type = "image/jpeg",
  quality?: number
): Promise<Blob> {
  return new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b!), type, quality)
  );
}

async function createZip(
  files: { name: string; content: Blob }[]
): Promise<Blob> {
  const jszip = new JSZip();
  for (const file of files) {
    jszip.file(file.name, new Uint8Array(await file.content.arrayBuffer()));
  }
  return jszip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
}
